import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { EmptyState, Spinner } from '@/components/ui';
import { AppShell } from '@/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const PatientListPage = lazy(() => import('@/pages/patients/PatientListPage'));
const PatientFormPage = lazy(() => import('@/pages/patients/PatientFormPage'));
const PatientDetailPage = lazy(() => import('@/pages/patients/PatientDetailPage'));
const AppointmentsPage = lazy(() => import('@/pages/appointments/AppointmentsPage'));
const AppointmentDashboardPage = lazy(() => import('@/pages/appointments/AppointmentDashboardPage'));
const QueuePage = lazy(() => import('@/pages/queue/QueuePage'));
const QueueDisplayPage = lazy(() => import('@/pages/queue/QueueDisplayPage'));
const QueueKioskPage = lazy(() => import('@/pages/queue/QueueKioskPage'));
const QueueReportPage = lazy(() => import('@/pages/queue/QueueReportPage'));
const QueueServicesPage = lazy(() => import('@/pages/admin/QueueServicesPage'));
const EncounterListPage = lazy(() => import('@/pages/encounters/EncounterListPage'));
const EncounterWorkspacePage = lazy(() => import('@/pages/encounters/EncounterWorkspacePage'));
const PharmacyPage = lazy(() => import('@/pages/pharmacy/PharmacyPage'));
const PrescriptionDetailPage = lazy(() => import('@/pages/pharmacy/PrescriptionDetailPage'));
const LabWorklistPage = lazy(() => import('@/pages/lab/LabWorklistPage'));
const LabOrderPage = lazy(() => import('@/pages/lab/LabOrderPage'));
const InvoiceListPage = lazy(() => import('@/pages/billing/InvoiceListPage'));
const InvoiceCreatePage = lazy(() => import('@/pages/billing/InvoiceCreatePage'));
const InvoiceDetailPage = lazy(() => import('@/pages/billing/InvoiceDetailPage'));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const DepartmentsPage = lazy(() => import('@/pages/admin/DepartmentsPage'));
const PractitionersPage = lazy(() => import('@/pages/admin/PractitionersPage'));
const HospitalPage = lazy(() => import('@/pages/admin/HospitalPage'));
const AuditPage = lazy(() => import('@/pages/admin/AuditPage'));
const TranslationsPage = lazy(() => import('@/pages/admin/TranslationsPage'));
const LanguagesPage = lazy(() => import('@/pages/admin/LanguagesPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const PathwaysPage = lazy(() => import('@/pages/pathways/PathwaysPage'));
const PathwayEditorPage = lazy(() => import('@/pages/pathways/PathwayEditorPage'));
const PatientAppAdminPage = lazy(() => import('@/pages/admin/PatientAppPage'));
const EyeRegistrationPrint = lazy(() => import('@/pages/print/EyeRegistrationPrint'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const LocationsPage = lazy(() => import('@/pages/admin/LocationsPage'));
const DiseasesPage = lazy(() => import('@/pages/admin/DiseasesPage'));
const TenantsPage = lazy(() => import('@/pages/admin/TenantsPage'));
const StaffKpiPage = lazy(() => import('@/pages/admin/StaffKpiPage'));
const StaffKpiTeamPage = lazy(() => import('@/pages/staff/StaffKpiTeamPage'));
const RolesPage = lazy(() => import('@/pages/admin/RolesPage'));
const DisabilityTypesPage = lazy(() => import('@/pages/admin/DisabilityTypesPage'));
const ReferralsPage = lazy(() => import('@/pages/clinical/Referrals'));
const FollowUpsPage = lazy(() => import('@/pages/clinical/FollowUps'));
const DuplicatesPage = lazy(() => import('@/pages/patients/DuplicatesPage'));
const ReferralPrint = lazy(() => import('@/pages/print/ReferralPrint'));
const NationalReportingPage = lazy(() => import('@/pages/national/NationalReportingPage'));
const EyeCenterPage = lazy(() => import('@/pages/eye/EyeCenterPage'));
const InventoryPage = lazy(() => import('@/pages/inventory/InventoryPage'));
const ItemDetailPage = lazy(() => import('@/pages/inventory/ItemDetailPage'));
const AssetDetailPage = lazy(() => import('@/pages/inventory/EquipmentTab'));
const PrFormPage = lazy(() => import('@/pages/inventory/PurchaseRequestPages').then((m) => ({ default: m.PrFormPage })));
const PrDetailPage = lazy(() => import('@/pages/inventory/PurchaseRequestPages').then((m) => ({ default: m.PrDetailPage })));
const PoFormPage = lazy(() => import('@/pages/inventory/PurchaseOrderPages').then((m) => ({ default: m.PoFormPage })));
const PoDetailPage = lazy(() => import('@/pages/inventory/PurchaseOrderPages').then((m) => ({ default: m.PoDetailPage })));
const PurchaseOrderPrint = lazy(() => import('@/pages/print/PurchaseOrderPrint'));
const EyeExamPage = lazy(() => import('@/pages/eye/EyeExamPage'));
const EyePatientPage = lazy(() => import('@/pages/eye/EyePatientPage'));
const EyeSurgeryPage = lazy(() => import('@/pages/eye/EyeSurgeryPage'));
const StockDocumentPage = lazy(() => import('@/pages/inventory/StockDocuments').then((m) => ({ default: m.StockDocumentPage })));
const StockDocumentPrint = lazy(() => import('@/pages/inventory/StockDocuments').then((m) => ({ default: m.StockDocumentPrint })));
const InpatientPage = lazy(() => import('@/pages/inpatient/InpatientPage'));
const AdmissionPage = lazy(() => import('@/pages/inpatient/AdmissionPage'));
const DischargePrint = lazy(() => import('@/pages/inpatient/AdmissionPage').then((m) => ({ default: m.DischargePrint })));
const ServicesPage = lazy(() => import('@/pages/services/ServicesPage'));
const ServiceOrderPage = lazy(() => import('@/pages/services/ServiceOrderPage'));
const ServiceReportPrint = lazy(() => import('@/pages/services/ServiceOrderPage').then((m) => ({ default: m.ServiceReportPrint })));
const PosPage = lazy(() => import('@/pages/pos/PosPage'));
const PosCatalogPage = lazy(() => import('@/pages/pos/CatalogPage'));
const PosStockPage = lazy(() => import('@/pages/pos/PosStockPage'));
const PosRefillPage = lazy(() => import('@/pages/pos/RefillPage'));
const SalesListPage = lazy(() => import('@/pages/pos/SalesPages').then((m) => ({ default: m.SalesListPage })));
const SaleDetailPage = lazy(() => import('@/pages/pos/SalesPages').then((m) => ({ default: m.SaleDetailPage })));
const SaleReceiptPrint = lazy(() => import('@/pages/pos/SalesPages').then((m) => ({ default: m.SaleReceiptPrint })));
const FinancePage = lazy(() => import('@/pages/finance/FinancePage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <Spinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function Guard({ perms, children }: { perms?: string[]; children: ReactNode }) {
  const { canAny } = useAuth();
  const { t } = useTranslation();
  if (perms && !canAny(...perms)) return <EmptyState>{t('errors.FORBIDDEN')}</EmptyState>;
  return <>{children}</>;
}

/** Users without dashboard access land on their first permitted module. */
function Home() {
  const { can, canAny } = useAuth();
  if (can('report.read')) return <DashboardPage />;
  // Kiosk / display device accounts open straight on the kiosk.
  if (can('queue.kiosk') && !can('queue.read')) return <Navigate to="/queue/kiosk" replace />;
  if (can('patient.read')) return <Navigate to="/patients" replace />;
  if (canAny('prescription.read', 'pharmacy.dispense')) return <Navigate to="/pharmacy" replace />;
  if (can('lab.read')) return <Navigate to="/lab" replace />;
  if (can('billing.read')) return <Navigate to="/billing" replace />;
  if (canAny('inventory.read', 'purchase.request', 'purchase.approve')) return <Navigate to="/inventory" replace />;
  if (can('sales.sell')) return <Navigate to="/pos" replace />;
  if (canAny('service.perform', 'service.verify')) return <Navigate to="/services" replace />;
  if (canAny('finance.read', 'finance.manage')) return <Navigate to="/finance" replace />;
  if (can('inpatient.read')) return <Navigate to="/inpatient" replace />;
  return <Navigate to="/settings" replace />;
}

function NotFound() {
  const { t } = useTranslation();
  return <EmptyState>{t('errors.NOT_FOUND')}</EmptyState>;
}

export function App() {
  const g = (perms: string[] | undefined, el: ReactNode) => <Guard perms={perms}>{el}</Guard>;
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/print/eye-registration/:patientId"
          element={<RequireAuth>{g(['patient.read'], <EyeRegistrationPrint />)}</RequireAuth>}
        />
        <Route
          path="/print/purchase-order/:id"
          element={<RequireAuth>{g(['inventory.read', 'purchase.order', 'purchase.approve'], <PurchaseOrderPrint />)}</RequireAuth>}
        />
        <Route
          path="/print/stock-document/:id"
          element={<RequireAuth>{g(['inventory.read', 'inventory.request'], <StockDocumentPrint />)}</RequireAuth>}
        />
        <Route path="/print/discharge/:id" element={<RequireAuth>{g(['inpatient.read'], <DischargePrint />)}</RequireAuth>} />
        <Route path="/print/referral/:id" element={<RequireAuth>{g(['referral.read', 'referral.manage'], <ReferralPrint />)}</RequireAuth>} />
        <Route path="/print/service-report/:id" element={<RequireAuth>{g(['service.order', 'service.perform', 'service.verify', 'service.manage', 'encounter.read'], <ServiceReportPrint />)}</RequireAuth>} />
        <Route path="/print/sale/:id" element={<RequireAuth>{g(['sales.sell', 'sales.void', 'finance.read'], <SaleReceiptPrint />)}</RequireAuth>} />
        <Route path="/queue/display" element={<RequireAuth>{g(['queue.read', 'queue.display'], <QueueDisplayPage />)}</RequireAuth>} />
        <Route
          path="/queue/display/:departmentId"
          element={<RequireAuth>{g(['queue.read', 'queue.display'], <QueueDisplayPage />)}</RequireAuth>}
        />
        <Route path="/queue/kiosk" element={<RequireAuth>{g(['queue.kiosk'], <QueueKioskPage />)}</RequireAuth>} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="patients" element={g(['patient.read'], <PatientListPage />)} />
          <Route path="patients/new" element={g(['patient.write'], <PatientFormPage key="new" />)} />
          <Route path="patients/duplicates" element={g(['patient.merge', 'patient.merge.approve'], <DuplicatesPage />)} />
          <Route path="referrals" element={g(['referral.read', 'referral.manage'], <ReferralsPage />)} />
          <Route path="follow-ups" element={g(['followup.manage', 'encounter.read', 'patient.read'], <FollowUpsPage />)} />
          <Route path="patients/:id" element={g(['patient.read'], <PatientDetailPage />)} />
          <Route path="patients/:id/edit" element={g(['patient.write'], <PatientFormPage key="edit" />)} />
          <Route path="appointments" element={g(['appointment.read'], <AppointmentsPage />)} />
          <Route path="appointments/dashboard" element={g(['appointment.read'], <AppointmentDashboardPage />)} />
          <Route path="queue" element={g(['queue.read'], <QueuePage />)} />
          <Route path="queue/report" element={g(['queue.read'], <QueueReportPage />)} />
          <Route path="admin/queue-services" element={g(['queue.configure'], <QueueServicesPage />)} />
          <Route path="encounters" element={g(['encounter.read'], <EncounterListPage />)} />
          <Route path="encounters/:id" element={g(['encounter.read'], <EncounterWorkspacePage />)} />
          <Route path="pharmacy" element={g(['prescription.read', 'pharmacy.dispense'], <PharmacyPage />)} />
          <Route path="pharmacy/:id" element={g(['prescription.read'], <PrescriptionDetailPage />)} />
          <Route path="lab" element={g(['lab.read'], <LabWorklistPage />)} />
          <Route path="lab/:id" element={g(['lab.read'], <LabOrderPage />)} />
          <Route path="eye" element={g(['ophthalmology.read'], <EyeCenterPage />)} />
          <Route path="eye/exams/:id" element={g(['ophthalmology.read'], <EyeExamPage />)} />
          <Route path="eye/patients/:id" element={g(['ophthalmology.read'], <EyePatientPage />)} />
          <Route path="eye/surgeries/:id" element={g(['ophthalmology.read'], <EyeSurgeryPage />)} />
          <Route path="pathways" element={g(['patient.read'], <PathwaysPage />)} />
          <Route path="pathways/new" element={g(['pathway.manage'], <PathwayEditorPage />)} />
          <Route path="pathways/:id" element={g(['patient.read'], <PathwayEditorPage />)} />
          <Route path="reports" element={g(['report.read'], <ReportsPage />)} />
          <Route path="national-reporting" element={g(['kpi.read', 'report.prepare', 'report.approve', 'dhis2.admin', 'dhis2.submit', 'dhis2.approve'], <NationalReportingPage />)} />
          <Route path="inventory" element={g(['inventory.read', 'purchase.request', 'purchase.approve', 'purchase.order', 'inventory.request', 'inventory.verify'], <InventoryPage />)} />
          <Route path="inventory/items/:id" element={g(['inventory.read'], <ItemDetailPage />)} />
          <Route path="inventory/documents/:id" element={g(['inventory.read', 'inventory.request'], <StockDocumentPage />)} />
          <Route path="inpatient" element={g(['inpatient.read', 'ward.manage'], <InpatientPage />)} />
          <Route path="inpatient/admissions/:id" element={g(['inpatient.read'], <AdmissionPage />)} />
          <Route path="services" element={g(['service.order', 'service.perform', 'service.verify', 'service.manage'], <ServicesPage />)} />
          <Route path="services/orders/:id" element={g(['service.order', 'service.perform', 'service.verify', 'service.manage', 'encounter.read'], <ServiceOrderPage />)} />
          <Route path="pos" element={g(['sales.sell'], <PosPage />)} />
          <Route path="pos/catalog" element={g(['pos.manage', 'pos.stock'], <PosCatalogPage />)} />
          <Route path="pos/stock" element={g(['pos.stock', 'pos.manage'], <PosStockPage />)} />
          <Route path="pos/refills/:id" element={g(['pos.stock', 'pos.manage', 'inventory.manage', 'inventory.verify', 'inventory.read'], <PosRefillPage />)} />
          <Route path="sales" element={g(['sales.sell', 'sales.void', 'finance.read'], <SalesListPage />)} />
          <Route path="sales/:id" element={g(['sales.sell', 'sales.void', 'finance.read'], <SaleDetailPage />)} />
          <Route path="finance" element={g(['finance.read', 'finance.manage', 'cash.session'], <FinancePage />)} />
          <Route path="inventory/assets/:id" element={g(['inventory.read'], <AssetDetailPage />)} />
          <Route path="inventory/requests/new" element={g(['purchase.request'], <PrFormPage />)} />
          <Route path="inventory/requests/:id" element={g(['inventory.read', 'purchase.request', 'purchase.approve', 'purchase.order'], <PrDetailPage />)} />
          <Route path="inventory/requests/:id/edit" element={g(['purchase.request'], <PrFormPage />)} />
          <Route path="inventory/orders/new" element={g(['purchase.order'], <PoFormPage />)} />
          <Route path="inventory/orders/:id" element={g(['inventory.read', 'purchase.request', 'purchase.approve', 'purchase.order'], <PoDetailPage />)} />
          <Route path="inventory/orders/:id/edit" element={g(['purchase.order'], <PoFormPage />)} />
          <Route path="admin/locations" element={g(['location.manage'], <LocationsPage />)} />
          <Route path="admin/diseases" element={g(['disease.manage'], <DiseasesPage />)} />
          <Route path="admin/patient-app" element={g(['portal.manage'], <PatientAppAdminPage />)} />
          <Route path="billing" element={g(['billing.read'], <InvoiceListPage />)} />
          <Route path="billing/new" element={g(['billing.write'], <InvoiceCreatePage />)} />
          <Route path="billing/:id" element={g(['billing.read'], <InvoiceDetailPage />)} />
          <Route path="admin/users" element={g(['user.read'], <UsersPage />)} />
          <Route path="admin/users/:id/kpi" element={g(['user.read', 'staffkpi.read', 'staffkpi.manage'], <StaffKpiPage />)} />
          <Route path="staff-kpi" element={g(['staffkpi.read', 'staffkpi.manage'], <StaffKpiTeamPage />)} />
          <Route path="admin/roles" element={g(['role.manage'], <RolesPage />)} />
          <Route path="admin/disability-types" element={g(['reference.manage'], <DisabilityTypesPage />)} />
          <Route path="my-kpi" element={<StaffKpiPage />} />
          <Route path="admin/departments" element={g(['org.manage'], <DepartmentsPage />)} />
          <Route path="admin/practitioners" element={g(['org.manage'], <PractitionersPage />)} />
          <Route path="admin/hospital" element={g(['org.manage'], <HospitalPage />)} />
          <Route path="admin/tenants" element={g(['tenant.manage'], <TenantsPage />)} />
          <Route path="admin/audit" element={g(['audit.read'], <AuditPage />)} />
          <Route path="admin/translations" element={<TranslationsPage />} />
          <Route path="admin/languages" element={g(['i18n.manage.global'], <LanguagesPage />)} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
